import { JSONSchema7 } from 'json-schema'
import isNull from 'lodash/isNull'
import { TakeableChannel } from 'redux-saga'
import { actionChannel, all, call, put, select, take } from 'redux-saga/effects'

import {
    GENERAL_CACHE,
    getFloorLevelEnumValues,
    getSchemaForFloorLevel,
    matchFloorLevelFromContext,
} from '../utils/floorLevelUtils'
import {
    createGroupFromType,
    generateMeasurementsToUpdate,
    handleCreateDrawableLocation2DError,
    isValidOpeningSize,
    resolveMeasuringScaleFactor,
    selectScaleFactor,
    updateDrawableGroupAndStore,
} from './createDrawableLocation'
import { convertOpeningsWithGroupIdsToDrawableLocations } from './data-prep/convertOpeningsToDrawableLocations'
import { bulkCreateOpenings, CreateOpeningResponse } from '../../../api/projects-api'
import { Coordinate } from '../../../models/activeDrawable'
import { ActiveFloor } from '../../../models/activeFloor'
import { Project } from '../../../models/project'
import {
    DRAWABLE_SETTINGS_TYPES,
    DRAWABLE_TYPES,
    IMUP_JOIST_TYPES_GROUP,
    NEW_IMUP_JOIST_TYPES_GROUP,
} from '../../../shared/constants/drawable-types'
import { convertScaleEnumToString } from '../../../utils/calculations/scaleConversion/scaleConversion'
import { buildCoordinatesFromPaperItem } from '../../../utils/coordinates/buildCoordinates'
import { defineLocationRegion } from '../../../utils/coordinates/defineLocationRegion'
import { getUnitOfMeasureByDrawingType } from '../../../utils/general'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Workspace } from '../../lib/toolBoxes/2D'
import {
    createMultipleDrawableLocations,
    getSelectedMaterial,
    selectNewCreatedDrawables,
    setMultipleNewCreatedDrawables,
} from '../../slices/2D'
import { setInfoModalData } from '../../slices/common'
import { selectActiveChunkScalesAndDPI, selectDrawableActiveFloor } from '../../slices/documents'
import { ActiveGeometryGroup, Geometry, selectActiveGeometryGroup, selectGeometries } from '../../slices/geometry'
import { selectProject } from '../../slices/project'
import { BulkOpeningCreationData, IMUP2DDrawableCutout, IMUP2DDrawableLocation } from '../../types'

type ParentSettings = {
    floor_level?: string
    location?: string
    secondary_location?: string
    source_measurement_id: number
}

// I-joist > Traditional joist > Traditional sleeper joist > Truss. Unknown types lose to any known type.
const FLOOR_TYPE_PRIORITY: string[] = [
    DRAWABLE_SETTINGS_TYPES.I_JOIST,
    DRAWABLE_SETTINGS_TYPES.TRADITIONAL_JOIST,
    DRAWABLE_SETTINGS_TYPES.TRADITIONAL_SLEEPER_JOIST,
    DRAWABLE_SETTINGS_TYPES.FLOOR_TRUSS,
]

// Normalizes type values to lowercase DRAWABLE_SETTINGS_TYPES strings for priority lookup
const TYPE_NORMALIZATION_MAP: Record<string, string> = {
    [DRAWABLE_TYPES.FLOOR_TRUSS_SYSTEM]: DRAWABLE_SETTINGS_TYPES.FLOOR_TRUSS,
    truss: DRAWABLE_SETTINGS_TYPES.FLOOR_TRUSS,
}

export function selectParentByTypePriority(candidateGeometries: Geometry[]): Geometry | null {
    if (candidateGeometries.length === 0) return null
    if (candidateGeometries.length === 1) return candidateGeometries[0]

    const getPriority = (geo: Geometry): number => {
        const rawType = (geo.openingSettings?.type ?? geo.type)?.toLowerCase()
        const normalizedType = TYPE_NORMALIZATION_MAP[rawType] ?? rawType
        const index = FLOOR_TYPE_PRIORITY.indexOf(normalizedType)

        return index === -1 ? FLOOR_TYPE_PRIORITY.length : index
    }

    return candidateGeometries.reduce((best, current) => (getPriority(current) < getPriority(best) ? current : best))
}

// Number of evenly-spaced sample points along a segment used to detect adjacent floor systems
const SAMPLE_COUNT = 5

// For each sample point, collect all area items within this multiplier of the closest distance.
// Ratio of 3 means: if the nearest item is at distance 4, also collect items up to distance 12.
// This ensures that when a line is drawn between two floor/roof systems (one at distance 4, one at 8),
// both are collected as candidates so type priority can pick the correct parent
const CANDIDATE_DISTANCE_RATIO = 3

// For a single sample point, measures the distance to every area item and adds all items
// within CANDIDATE_DISTANCE_RATIO × the minimum distance to closestPerSample.
// The ratio threshold ensures that when a segment crosses a boundary between two floor/roof
// systems, candidates from both sides are collected so type priority can pick the right parent.
export function collectSampleCandidates(samplePoint: paper.Point, areaItems: paper.Item[]): Set<paper.Item> {
    const sampleDistances = new Map<paper.Item, number>()

    for (const areaItem of areaItems) {
        const pathItem = areaItem as paper.PathItem

        if (typeof pathItem.getNearestPoint !== 'function') continue

        sampleDistances.set(areaItem, pathItem.getNearestPoint(samplePoint).getDistance(samplePoint))
    }

    const minSampleDistance = Math.min(...sampleDistances.values())
    const threshold = Math.max(minSampleDistance * CANDIDATE_DISTANCE_RATIO, 1)

    return [...sampleDistances.entries()].reduce((candidates, [item, distance]) => {
        if (distance <= threshold) candidates.add(item)

        return candidates
    }, new Set<paper.Item>())
}

// All drawing types that represent floor/roof systems — only these should be considered
// when determining the parent for a rimboard segment from the perimeter tool.
const FLOOR_ROOF_SYSTEM_TYPES: ReadonlySet<string> = new Set<string>([
    ...IMUP_JOIST_TYPES_GROUP,
    ...NEW_IMUP_JOIST_TYPES_GROUP,
    DRAWABLE_TYPES.FLOOR_TRUSS_SYSTEM,
])

// For the perimeter tool: finds the closest floor/roof system to the segment's center point.
// No type priority — pure proximity. Only floor/roof system materials are considered.
export function getClosestFloorRoofSystemSettings(
    paperItem: paper.Item,
    geometries: Geometry[],
    workspaceTool: Workspace
): ParentSettings | null {
    const areaItems: paper.Item[] = workspaceTool.getItemsWithCriteria(
        'data',
        (data) =>
            data?.isEnabledForBorderTool === true &&
            data?.opening_group_id != null &&
            FLOOR_ROOF_SYSTEM_TYPES.has(data?.drawing_type)
    )

    if (areaItems.length === 0) return null

    const path = paperItem as paper.Path
    const centerPoint = path.getPointAt(path.length / 2)

    let closestItem: paper.Item | null = null
    let closestDistance = Infinity

    for (const areaItem of areaItems) {
        const pathItem = areaItem as paper.PathItem

        if (typeof pathItem.getNearestPoint !== 'function') continue

        const distance = pathItem.getNearestPoint(centerPoint).getDistance(centerPoint)

        if (distance < closestDistance) {
            closestDistance = distance
            closestItem = areaItem
        }
    }

    if (!closestItem) return null

    const geo = geometries.find((g) => g.opening_id === closestItem!.data.drawable_id)

    return geo ? buildParentSettings(geo) : null
}

function buildParentSettings(geometry: Geometry): ParentSettings {
    const { floor_level, location, secondary_location } = geometry.openingSettings ?? {}

    return {
        ...(floor_level && { floor_level }),
        ...(location && { location }),
        ...(secondary_location && { secondary_location }),
        source_measurement_id: geometry.opening_id,
    }
}

export function getParentSettings(
    paperItem: paper.Item,
    geometries: Geometry[],
    workspaceTool: Workspace
): ParentSettings | null {
    const areaItems: paper.Item[] = workspaceTool.getItemsWithCriteria(
        'data',
        (data) =>
            data?.isEnabledForBorderTool === true &&
            data?.opening_group_id != null &&
            data?.drawing_type !== DRAWABLE_TYPES.RIMBOARD
    )

    if (areaItems.length === 0) return null

    const path = paperItem as paper.Path

    const samplePoints = Array.from({ length: SAMPLE_COUNT }, (_, i) =>
        path.getPointAt((i / (SAMPLE_COUNT - 1)) * path.length)
    )

    const closestPerSample = new Set(samplePoints.flatMap((pt) => [...collectSampleCandidates(pt, areaItems)]))

    const candidateGeometries = [...closestPerSample].reduce<Geometry[]>((acc, areaItem) => {
        const geo = geometries.find((g) => g.opening_id === areaItem.data.drawable_id)

        if (geo) acc.push(geo)

        return acc
    }, [])

    const selected = selectParentByTypePriority(candidateGeometries)

    return selected ? buildParentSettings(selected) : null
}

/**
 * Creates multiple drawable locations in bulk for better performance.
 * This replaces the previous approach of creating drawables one at a time.
 */
export function* bulkCreateDrawableLocations(action: ReturnType<typeof createMultipleDrawableLocations>) {
    const manager: PaperManager = yield call(managers.get2DManager)

    if (!manager) return

    // Collect required state and configuration
    const selectedMaterialType: DRAWABLE_TYPES | null = yield select(getSelectedMaterial)
    const newCreatedDrawablesIds = yield select(selectNewCreatedDrawables)
    let activeDrawableGroup: ActiveGeometryGroup | null = yield select(selectActiveGeometryGroup)
    const activeFloor: ActiveFloor = yield select(selectDrawableActiveFloor)
    const project: Project = yield select(selectProject)
    const scaleFactor: number = yield select(selectScaleFactor)
    const scaleData = yield select(selectActiveChunkScalesAndDPI)
    const { dpi, xCalibrationFactor, yCalibrationFactor, pdfScale } = scaleData

    const workspaceTool: Workspace = yield call(manager.getTool, Workspace.NAME)

    // Collect paper items from the provided IDs
    const paperItems: paper.Item[] = []

    for (const itemId of action.payload) {
        const item: paper.Item | null = yield call(workspaceTool.getItemWithPaperId, itemId)

        if (item) {
            paperItems.push(item)
        }
    }

    if (!paperItems.length) {
        console.warn('No valid paper items found for the provided IDs')

        return
    }

    // For rimboard, inherit floor_level, location, secondary_location from the parent material's group
    const isRimboard = selectedMaterialType === DRAWABLE_TYPES.RIMBOARD
    const geometries: Geometry[] | null = isRimboard ? yield select(selectGeometries) : null
    // Use the first segment to derive group-level settings (floor_level, location, secondary_location)
    const primaryParentSettings =
        isRimboard && geometries ? getClosestFloorRoofSystemSettings(paperItems[0], geometries, workspaceTool) : null

    // Get schema to auto-detect floor_level from region or page name
    const regions = activeFloor.document_chunk.regions
    const buildingId = activeFloor?.building_id?.toString() ?? GENERAL_CACHE
    const schemaForFloorLevel: JSONSchema7 | null = yield call(getSchemaForFloorLevel, selectedMaterialType, buildingId)

    const floorLevelEnumValues = getFloorLevelEnumValues(schemaForFloorLevel)
    const firstItemRegionId = floorLevelEnumValues
        ? defineLocationRegion(paperItems[0] as paper.Path, regions, workspaceTool)
        : null
    const autoFloorLevelForGroup = floorLevelEnumValues
        ? matchFloorLevelFromContext(floorLevelEnumValues, firstItemRegionId, regions, activeFloor.page_name)
        : null

    // Ensure we have a drawable group to work with
    const isNewActiveDrawableGroup = activeDrawableGroup === null
    let drawableGroupId = activeDrawableGroup?.id ?? null

    if (drawableGroupId === null && !isNull(selectedMaterialType)) {
        const parentSettingsWithoutSourceMeasurementId = primaryParentSettings
            ? (({ source_measurement_id: _ignored, ...rest }) => rest)(primaryParentSettings)
            : {}

        const firstItemSettings = {
            ...paperItems[0].data.aiSuggestion?.settings,
            shape_type: paperItems[0].data.shapeType,
            ...(autoFloorLevelForGroup && { floor_level: autoFloorLevelForGroup }),
            ...parentSettingsWithoutSourceMeasurementId,
        }

        const newlyCreatedActiveDrawableGroup: ActiveGeometryGroup | null = yield call(
            createGroupFromType,
            selectedMaterialType,
            project,
            firstItemSettings
        )

        drawableGroupId = newlyCreatedActiveDrawableGroup?.id ?? null
        activeDrawableGroup = newlyCreatedActiveDrawableGroup
    }

    if (isNull(drawableGroupId)) {
        console.warn('Failed to create or find drawable group')
        // Cleanup paper items since we cannot proceed
        yield all(paperItems.map((item) => call(workspaceTool.removeItemWithPaperId, item.id)))

        return
    }

    // Prepare constants for bulk creation
    const activeFloorHash = activeFloor.hash
    const projectId = project.id
    const documentChunkId = activeFloor.document_chunk.id
    const openingsToCreate: BulkOpeningCreationData[] = []

    // Process each paper item to prepare opening data
    for (const paperItem of paperItems) {
        const path = paperItem as paper.Path
        // Each border segment independently finds its closest floor/roof system for source_measurement_id
        const itemParentSettings =
            isRimboard && geometries ? getClosestFloorRoofSystemSettings(paperItem, geometries, workspaceTool) : null
        const coordinates: Coordinate[] = yield call(buildCoordinatesFromPaperItem, paperItem, path)
        const regionId = defineLocationRegion(path, regions, workspaceTool)

        const measurements = generateMeasurementsToUpdate({
            path,
            settingsAndType: {
                type: activeDrawableGroup?.type,
                settings: activeDrawableGroup?.settings,
            },
            scaleFactor: resolveMeasuringScaleFactor(regions, regionId, scaleFactor),
            xCalibrationFactor,
            yCalibrationFactor,
            dpi,
            pdfScale,
            coordinates,
        })

        const isValidShapeSize: boolean = yield call(isValidOpeningSize, measurements, paperItem.data.shapeType)

        if (!isValidShapeSize) {
            yield put(
                setInfoModalData({
                    visible: true,
                    title: 'Error',
                    body: 'Material is too small and could not be created that size.',
                })
            )
            yield call(handleCreateDrawableLocation2DError, paperItem, isNewActiveDrawableGroup)

            return
        }

        // Prepare additional data with scale factor information
        const additionalData: Record<string, any> = {}

        if (paperItem.data.parentSystem) {
            additionalData.parent_system = paperItem.data.parentSystem
        }

        if (regionId) {
            const regionPath = workspaceTool.getRegionItemByRegionId(regionId)

            additionalData.scale_factor = convertScaleEnumToString(regionPath.data.scale)
        } else {
            additionalData.scale_factor = convertScaleEnumToString(activeFloor.scale_factor)
        }

        const openingData: BulkOpeningCreationData = {
            opening_group_id: drawableGroupId,
            coordinates,
            cutouts: paperItem.data.cutouts as IMUP2DDrawableCutout[],
            document_chunk_id: documentChunkId,
            measurements,
            settings: {
                unit_of_measure: getUnitOfMeasureByDrawingType(path.data.shapeType),
                shape_type: path.data.shapeType,
                ...itemParentSettings,
            },
            region_id: regionId,
            additional_data: additionalData,
            floor_hash: activeFloorHash,
        }

        openingsToCreate.push(openingData)
    }
    const createdDrawables: CreateOpeningResponse[] = yield call(bulkCreateOpenings, projectId, openingsToCreate)

    yield put(setMultipleNewCreatedDrawables(createdDrawables.map((opening) => opening.id)))

    const drawableLocations: IMUP2DDrawableLocation[] = yield call(
        convertOpeningsWithGroupIdsToDrawableLocations,
        createdDrawables
    )

    if (activeDrawableGroup) {
        yield call(updateDrawableGroupAndStore, {
            drawableLocations,
            createdDrawables,
            activeDrawableGroup: activeDrawableGroup,
            newCreatedDrawablesIds,
            drawableGroupId,
            paperItems,
        })
    }
}

export function* watchForBulkCreateDrawableLocations(isTesting?: boolean) {
    const creationRequestChannel = yield actionChannel(createMultipleDrawableLocations.type)

    while (true) {
        const action = yield take(
            creationRequestChannel as TakeableChannel<ReturnType<typeof createMultipleDrawableLocations>>
        )

        try {
            yield call(bulkCreateDrawableLocations, action as ReturnType<typeof createMultipleDrawableLocations>)
        } catch (error) {
            yield call(console.error, 'Error in bulkCreateDrawableLocations:', error)
        }

        if (isTesting) {
            break
        }
    }
}
